/*
 * swipe.js
 *
 * Swipe-naar-links/rechts gedrag op item-rijen (vast/var, inkomsten, extras).
 * Beide richtingen tonen hetzelfde actie-menu (Bewerken + Verwijderen).
 *
 * Werking:
 *  - Document-level pointer-listeners (1 paar listeners voor de hele app).
 *  - Drag-start op een element met .swipe-front → track horizontal offset.
 *  - Pointermove: zodra |dx| > |dy| + threshold, locken we de gesture als
 *    horizontaal en blokkeren vertical scroll.
 *  - Pointerup: snap naar open (±OPEN_PX) als |dx| > SNAP_PX, anders dicht.
 *  - Open een andere rij → sluit de vorige automatisch.
 *  - Klik op een actie-knop binnen .swipe-actions → sluit zichzelf en
 *    laat de bestaande onclick z'n werk doen.
 *  - Klik op de front terwijl open → eerst sluiten, geen propagatie.
 */

const ACT_PX      = 48;    // breedte van één swipe-actie-knop (matcht .swipe-act in CSS)
const ACT_PAD_PX  = 8;     // beetje extra zodat de rand-knop niet kaft tegen de scherm-rand
const FALLBACK_PX = 120;   // safety-net wanneer we de actie-knoppen niet vinden
const DIR_LOCK_PX = 8;     // hoeveel pixels voor we richting bepalen

let active = null;            // huidige .swipe-front waar de pointer op zit
let startX = 0, startY = 0;   // pointerdown-positie
let baseTx = 0;               // transform-offset op pointerdown
let locked = null;            // 'h' (horizontaal) | 'v' (verticaal) | null
let pointerId = null;
let openPx = FALLBACK_PX;     // open-distance voor de huidige gesture (per rij berekend)
let snapPx = 60;              // drempel om "open te willen" snappen (half van openPx)

/** Bepaal hoe ver deze rij open mag — afhankelijk van hoeveel actie-knoppen
 *  er in zitten. Dromen hebben edit+pause+del (3), vast/var/inkomst hebben
 *  edit+del (2), schulden+extra hebben er soms maar 1. */
function openDistanceFor(front) {
  const wrap = front?.closest('.swipe-wrap');
  if (!wrap) return FALLBACK_PX;
  // Tel knoppen in één side (beide sides hebben dezelfde set).
  const side = wrap.querySelector('.swipe-side');
  const count = side ? side.querySelectorAll('.swipe-act').length : 0;
  if (!count) return FALLBACK_PX;
  return count * ACT_PX + ACT_PAD_PX;
}

let openRow = null;           // element dat momenteel open is

function setTx(el, tx, animated) {
  el.style.transition = animated ? 'transform .2s ease' : 'none';
  el.style.transform  = `translateX(${tx}px)`;
  el.dataset.tx = String(tx);
}

function closeRow(row, animated = true) {
  if (!row) return;
  setTx(row, 0, animated);
  row.classList.remove('swipe-open');
  if (openRow === row) openRow = null;
}

function openRowTo(row, direction, distance) {
  if (openRow && openRow !== row) closeRow(openRow);
  const open = distance || openDistanceFor(row);
  const tx = direction > 0 ? open : -open;
  setTx(row, tx, true);
  row.classList.add('swipe-open');
  openRow = row;
}

function onPointerDown(e) {
  // Niet starten als de klik op een actie-knop in de swipe-actions is.
  if (e.target.closest('.swipe-act')) return;

  const front = e.target.closest('.swipe-front');
  if (!front) {
    // Klik buiten een swipe-rij → sluit de eventuele open rij.
    if (openRow && !e.target.closest('.swipe-wrap')) closeRow(openRow);
    return;
  }

  // Als deze rij open is en de gebruiker klikt op de front (niet actie),
  // sluit hem en stop propagatie zodat inline onclicks niet vuren.
  if (front === openRow && e.type === 'pointerdown') {
    // Nog niet sluiten — wachten op tap (geen drag). Markeer voor pointerup.
    front.dataset.willClose = '1';
  }

  active   = front;
  startX   = e.clientX;
  startY   = e.clientY;
  baseTx   = parseFloat(active.dataset.tx) || 0;
  locked   = null;
  pointerId = e.pointerId;
  openPx   = openDistanceFor(active);
  snapPx   = Math.max(40, Math.round(openPx * 0.4));   // ~40% van open = "snap"-drempel
  active.style.transition = 'none';
}

function onPointerMove(e) {
  if (!active || e.pointerId !== pointerId) return;
  const dx = e.clientX - startX;
  const dy = e.clientY - startY;

  // Bepaal richting na DIR_LOCK_PX beweging
  if (!locked) {
    if (Math.abs(dx) < DIR_LOCK_PX && Math.abs(dy) < DIR_LOCK_PX) return;
    locked = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
    if (locked === 'h') {
      // Capture deze pointer zodat we niet onverwacht losgelaten worden.
      try { active.setPointerCapture(pointerId); } catch {}
    }
  }
  if (locked !== 'h') return;

  e.preventDefault();              // blokkeer vertical scroll
  active.dataset.willClose = '';   // niet meer "tap-to-close" — gebruiker sleept

  let tx = baseTx + dx;
  // Zachte rubber-band buiten de open-grens (per-rij berekend)
  if (tx >  openPx) tx =  openPx + (tx - openPx) * 0.3;
  if (tx < -openPx) tx = -openPx + (tx + openPx) * 0.3;
  setTx(active, tx, false);
}

function onPointerUp(e) {
  if (!active || e.pointerId !== pointerId) return;

  // Tap-to-close: open rij, geen drag → sluiten en stop event-bubbel
  if (active.dataset.willClose === '1' && locked !== 'h') {
    closeRow(active);
    e.stopPropagation();
    active.dataset.willClose = '';
    active = null;
    locked = null;
    return;
  }

  if (locked === 'h') {
    const tx = parseFloat(active.dataset.tx) || 0;
    if (Math.abs(tx) >= snapPx) {
      openRowTo(active, tx > 0 ? 1 : -1, openPx);
    } else {
      closeRow(active);
    }
  }

  active = null;
  locked = null;
  pointerId = null;
}

function onActionClick(e) {
  const btn = e.target.closest('.swipe-act');
  if (!btn) return;
  const wrap = btn.closest('.swipe-wrap');
  const front = wrap?.querySelector('.swipe-front');
  if (front) closeRow(front, false);
  // De inline onclick van de knop doet de rest (toggleEdit / delItem / etc.)
}

/** Init: hang document-level listeners op. Idempotent. */
export function initSwipe() {
  if (window.__swipeInit) return;
  window.__swipeInit = true;
  document.addEventListener('pointerdown', onPointerDown);
  document.addEventListener('pointermove', onPointerMove, { passive: false });
  document.addEventListener('pointerup',   onPointerUp);
  document.addEventListener('pointercancel', onPointerUp);
  document.addEventListener('click',       onActionClick, true);
}

/** Reset open-row tracking — aanroepen na een full render() zodat we
 *  niet naar een verdwenen DOM-node blijven verwijzen. */
export function resetOpenRow() {
  openRow = null;
  active = null;
  locked = null;
}
